/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//Standard libraries
import React, { useMemo } from "react";
import { LazyLoadComponent } from "react-lazy-load-image-component";
//Custom libraries
import styled from "@mui/styled-engine-sc";
import { StyledBox } from "../box";
import { StyledProgressPlaceholder } from "../circular-progress";
import { StyledTypography } from "../typography";
import { StyledCard, StyledCardMedia } from "../card";
import { StyledCheckbox } from "../check-box";
import { StyledFormControlLabel } from "../form";
import { StyledLink } from "../link";
import { StyledGrid, StyledRadioGroup } from "@hcl-commerce-store-sdk/react-component";
import { divide } from "lodash";
import { StyledButton, StyledRadio } from "..";
import { useTranslation } from "react-i18next";

import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { convertirFloat } from "utils/convertirFloat";

// import { RibbonAd } from "../../components/ribbon-ad";

const StyledProductCard = styled(({ className, ...props }) =>
  props.to ? <StyledLink className={className} {...props} /> : <StyledBox className={className} {...props} />
)`
  ${({ theme }) => `
    display: block;
    width: 100%;

    >.product-card.w-compare-box {
      height: calc(100% + 2.625rem);
    }

    &.product-card {
      flex: 1;
      border-radius: 2px !important;
    }

    &.product-grid {
      .MuiCard-root {
        height: 100%;
      }
    }

  `}
`;

const FlexWrapper = styled("div")`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    justify-content: center;
    align-items: center;

    > label {
      > span.compare-checkbox {
        justify-content: center;
      }
    }

    .product-card-favorite-button {
      position: absolute;
      top: 2%;
      right: 5%;
    }

    .product-card-favorite-icon {
      font-size: 28px;
    }

    .sale-promo-container {
      top: 10px;
      left: 10px;
      position: absolute;
      display: flex;
    }

    .sale-promo-label {
      font-size: 11px;
      font-family: Inter;
      background: white;
      padding: 3px 6px;
      border: 1px solid;
      border-radius: 13px;

      display: flex;
      justify-content: center;
      align-items: center;
      align-content: center;
    }

    .price-before-sale-number-text {
      font-size: 15px;
      font-family: Inter;
      margin-right: 5px;
      color: #767677;
      text-decoration: line-through;
    }

    .price-number-text {
      font-size: 17px;
      font-family: Inter;
      margin-right: 5px;
      font-variation-settings: 'wght' 600;
      color: ${theme.palette.primary.main};
    }

    .sale-percentage-number-text {
      font-size: 15px;
      color: green;
      font-family: Inter;
    }

    .prouct-name-text {
      font-size: 14px;
      font-family: Inter;
      line-height: 1.2;
      margin-bottom: 7px;
      font-variation-settings: 'wght' 500;
      color: ${theme.palette.primary.main};
    }

    .product-usage-sale-text {
      color: #767677;
      font-size: 13px;
      font-family: Inter;
    }

  `}
`;

const CompareBox = ({ compare, product, catentryId }) => {
  const { type: t, partNumber } = product;
  const visibility = t === "product" || t === "item" || t === "variant" ? "visible" : "hidden";
  return (
    <div style={{ marginLeft: "16px", visibility }}>
      <StyledFormControlLabel
        control={
          <StyledCheckbox
            data-testid={`product-compare-${partNumber?.toLowerCase()}-checkbox`}
            className="compare-checkbox"
            disabled={!!(!compare.data.checked[catentryId] && compare.data.disabled)}
            checked={!!compare.data.checked[catentryId]}
            onChange={(e) => compare.onChange(e, catentryId, product)}
          />
        }
        label={<StyledTypography variant="caption">{compare.t(compare.labels.addTo)}</StyledTypography>}
      />
    </div>
  );
};

interface ProductCardProps {
  seoUrl: any;
  name: any;
  ribbonads: any[];
  thumbnail: any;
  thumbnailLoading?: boolean;
  price?: number | null;
  swatches: any[];
  catentryId?: string;
  onClick?: any;
  actions?: any;
  className?: any;
  categoryId?: string;
  formattedPriceDisplay: any;
  link?: any;
  compare?: any;
  product?: any;
  isB2B: boolean;
}

/**
 * Product Card component
 * displays catentry image, name, price, etc
 * @param props
 */
function ProductCard(props: ProductCardProps) {
  const {
    seoUrl,
    name,
    ribbonads,
    thumbnail,
    categoryId = "",
    thumbnailLoading = false,
    price,
    swatches,
    onClick,
    catentryId = "",
    formattedPriceDisplay,
    actions: cardActions = null,
    link = null,
    compare,
    product,
  } = props;

  const to = useMemo(() => link?.pathname ?? seoUrl, [link, seoUrl]);
  const linkState = useMemo(() => link?.state ?? { categoryId }, [link, categoryId]);
  const onClickAction = useMemo(() => (onClick ? { onClick } : {}), [onClick]);

  const uso = product?.attributes ? product?.attributes.filter((atribute: any) => atribute.identifier === "Uso") : "";
  const oferta = product?.attributes
    ? product?.attributes.filter((atribute: any) => atribute.identifier === "Oferta")
    : "";

  const precioOriginal = product.price[0].value ? convertirFloat(parseInt(product.price[0].value)) : null;

  const numberFormattedPrice = convertirFloat(formattedPriceDisplay?.props?.min);

  const colores = product?.attributes
    ? product?.attributes.filter((atribute: any) => atribute.identifier === "Color")
    : "";

  const { t } = useTranslation();
  /* const salePercentage: number = +oferta[0].values[0].value;
  const numberFormattedPrice: number = formattedPriceDisplay; */

  function imageExists(url) {
    const http = new XMLHttpRequest();
    http.open("HEAD", url, false);
    http.send();
    return http.status;
  }

  const getSeoHref = (partNumber) => {
    let img = "";
    partNumber = partNumber.substring(2).split("-");
    const url = `/wcsstore/ExtendedSitesCatalogAssetStore/images/catalog/default/${partNumber[0]}-${partNumber[1]}V1.jpg`;
    const exist = imageExists(url);
    if (exist === 200) {
      img = "https://dpjye2wk9gi5z.cloudfront.net" + url;
    } else {
      img = "null";
    }
    return img;
  };

  const contentComponent = (
    <>
      {thumbnailLoading ? (
        <StyledProgressPlaceholder className="vertical-padding-8" />
      ) : (
        <LazyLoadComponent visibleByDefault={(window as any).__isPrerender__ || false}>
          <div title={name}>
            <img src={getSeoHref(product.partNumber)} alt="" />
          </div>
        </LazyLoadComponent>
      )}
      <StyledGrid
        style={{ display: "flex", flexDirection: "column", alignItems: "start", padding: "0px 11px 11px 11px" }}>
        {/* <StyledGrid style={{ display: "flex" }}>
          {colores.length >= 1 &&
            colores.map((colorProp) =>
              colorProp.values[0]?.value === "Blanco" ? (
                <>
                  <div style={{ display: "flex" }}>
                    <div
                      style={{
                        background: "white",
                        borderRadius: "50%",
                        width: "22px",
                        height: "22px",
                        marginRight: "10px",
                        border: "solid 1px #CCCCCC",
                      }}></div>
                    <div
                      style={{
                        borderRadius: "50%",
                        width: "19px",
                        height: "19px",
                        marginRight: "10px",
                        marginTop: "1.5px",
                        marginLeft: "1.5px",
                        position: "absolute",
                        background: "white",
                      }}></div>
                  </div>
                </>
              ) : colorProp.values[0]?.value === "Amarillo" ? (
                <div style={{ display: "flex" }}>
                  <div
                    style={{
                      background: "white",
                      borderRadius: "50%",
                      width: "22px",
                      height: "22px",
                      marginRight: "10px",
                      border: "solid 1px #CCCCCC",
                    }}></div>
                  <div
                    style={{
                      borderRadius: "50%",
                      width: "19px",
                      height: "19px",
                      marginRight: "10px",
                      marginTop: "1.5px",
                      marginLeft: "1.5px",
                      position: "absolute",
                      background: "#efbf2a",
                    }}></div>
                </div>
              ) : colorProp.values[0]?.value === "Negro" ? (
                <div style={{ display: "flex" }}>
                  <div
                    style={{
                      background: "white",
                      borderRadius: "50%",
                      width: "23px",
                      height: "23px",
                      marginRight: "10px",
                      border: "solid 1px #CCCCCC",
                    }}></div>
                  <div
                    style={{
                      borderRadius: "50%",
                      width: "19px",
                      height: "19px",
                      marginRight: "10px",
                      marginTop: "2px",
                      marginLeft: "2px",
                      position: "absolute",
                      background: "black",
                    }}></div>
                </div>
              ) : colorProp.values[0]?.value === "Rosa" ? (
                <div style={{ display: "flex" }}>
                  <div
                    style={{
                      background: "white",
                      borderRadius: "50%",
                      width: "23px",
                      height: "23px",
                      marginRight: "10px",
                      border: "solid 1px #CCCCCC",
                    }}></div>
                  <div
                    style={{
                      borderRadius: "50%",
                      width: "19px",
                      height: "19px",
                      marginRight: "10px",
                      marginTop: "2px",
                      marginLeft: "2px",
                      position: "absolute",
                      background: "pink",
                    }}></div>
                </div>
              ) : colorProp.values[0]?.value === "Rojo" ? (
                <div style={{ display: "flex" }}>
                  <div
                    style={{
                      background: "white",
                      borderRadius: "50%",
                      width: "23px",
                      height: "23px",
                      marginRight: "10px",
                      border: "solid 1px #CCCCCC",
                    }}></div>
                  <div
                    style={{
                      borderRadius: "50%",
                      width: "19px",
                      height: "19px",
                      marginRight: "10px",
                      marginTop: "2px",
                      marginLeft: "2px",
                      position: "absolute",
                      background: "#A54949",
                    }}></div>
                </div>
              ) : colorProp.values[0]?.value === "Verde" ? (
                <div style={{ display: "flex" }}>
                  <div
                    style={{
                      background: "white",
                      borderRadius: "50%",
                      width: "23px",
                      height: "23px",
                      marginRight: "10px",
                      border: "solid 1px #CCCCCC",
                    }}></div>
                  <div
                    style={{
                      borderRadius: "50%",
                      width: "19px",
                      height: "19px",
                      marginRight: "10px",
                      marginTop: "2px",
                      marginLeft: "2px",
                      position: "absolute",
                      background: "#005D32",
                    }}></div>
                </div>
              ) : colorProp.values[0]?.value === "Violeta" ? (
                <div style={{ display: "flex" }}>
                  <div
                    style={{
                      background: "white",
                      borderRadius: "50%",
                      width: "23px",
                      height: "23px",
                      marginRight: "10px",
                      border: "solid 1px #CCCCCC",
                    }}></div>
                  <div
                    style={{
                      borderRadius: "50%",
                      width: "19px",
                      height: "19px",
                      marginRight: "10px",
                      marginTop: "2px",
                      marginLeft: "2px",
                      position: "absolute",
                      background: "violet",
                    }}></div>
                </div>
              ) : colorProp.values[0]?.value === "Café" ? (
                <div style={{ display: "flex" }}>
                  <div
                    style={{
                      background: "white",
                      borderRadius: "50%",
                      width: "23px",
                      height: "23px",
                      marginRight: "10px",
                      border: "solid 1px #CCCCCC",
                    }}></div>
                  <div
                    style={{
                      borderRadius: "50%",
                      width: "19px",
                      height: "19px",
                      marginRight: "10px",
                      marginTop: "2px",
                      marginLeft: "2px",
                      position: "absolute",
                      background: "brown",
                    }}></div>
                </div>
              ) : (
                <div style={{ display: "flex" }}>
                  <div
                    style={{
                      background: "white",
                      borderRadius: "50%",
                      width: "23px",
                      height: "23px",
                      marginRight: "10px",
                      border: "solid 1px #CCCCCC",
                    }}></div>
                  <div
                    style={{
                      borderRadius: "50%",
                      width: "19px",
                      height: "19px",
                      marginRight: "10px",
                      marginTop: "2px",
                      marginLeft: "2px",
                      position: "absolute",
                      background: "#5252ff",
                    }}></div>
                </div>
              )
            )}
        </StyledGrid> */}

        {oferta.length === 1 ? (
          <>
            <StyledGrid alignItems="center" style={{ display: "flex", marginTop: "5px", marginBottom: "5px" }}>
              {/* precio original */}
              {precioOriginal ? (
                <StyledTypography variant="body1" className="price-before-sale-number-text">
                  {precioOriginal}
                </StyledTypography>
              ) : null}

              {/* precio con descuento */}
              {numberFormattedPrice !== null ? (
                <StyledTypography className="price-number-text">{numberFormattedPrice}</StyledTypography>
              ) : null}

              {/* descuento */}
              {oferta[0].values[0].value ? (
                <StyledTypography className="sale-percentage-number-text">
                  -{oferta[0].values[0].value}
                </StyledTypography>
              ) : null}
            </StyledGrid>
          </>
        ) : (
          <StyledGrid alignItems="center" style={{ display: "flex", marginTop: "5px", marginBottom: "5px" }}>
            <StyledTypography variant="body1" align="left" className="price-number-text">
              {numberFormattedPrice}
            </StyledTypography>
          </StyledGrid>
        )}

        {/* nombre del producto */}
        <StyledTypography align="left" className="prouct-name-text">
          {name}
        </StyledTypography>

        {/* uso del producto */}
        {uso && <StyledTypography className="product-usage-sale-text">{uso[0]?.values[0]?.value}</StyledTypography>}

        {/* oferta del producto */}
        {oferta.length > 0 && (
          <StyledTypography className="product-usage-sale-text">{oferta[0]?.name}</StyledTypography>
        )}
      </StyledGrid>
      {swatches.length > 0 && <StyledTypography align="center">{swatches}</StyledTypography>}
    </>
  );

  const compareBox = useMemo(
    () => (compare ? <CompareBox {...{ compare, product, catentryId }} /> : <></>),
    [compare, product, catentryId]
  );

  return (
    <FlexWrapper>
      <StyledGrid container justifyContent="flex-start" className="sale-promo-container">
        {ribbonads.map(({ identifier, value }: any, i) => (
          <StyledGrid item container xs={12} justifyContent="flex-start">
            <StyledGrid item style={{ padding: "2px" }}>
              <div className="sale-promo-label"> {value === "Si" ? t("productDetail.NewRibbon") : value} </div>
            </StyledGrid>
          </StyledGrid>
        ))}
      </StyledGrid>

      <div className="product-card-favorite-button">
        <FavoriteBorderIcon className="product-card-favorite-icon" />
      </div>
      <StyledProductCard
        className="product-card"
        to={to}
        state={linkState}
        id={catentryId ? `productCard_a_1_${catentryId}` : ""}
        {...onClickAction}>
        <StyledCard
          testId={catentryId}
          {...{
            className: `product-card ${compare ? "w-compare-box" : ""}`,
            contentComponent,
            cardActions,
          }}
        />
      </StyledProductCard>
      {compareBox}
    </FlexWrapper>
  );
}
export default ProductCard;
